import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Notification,
  NotificationVariants,
  Pagination,
  SearchInput,
  Tag,
  TagAppearance,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { getBenefitActivityDashboardSagaAction } from "../../../../../../redux/saga/partner-program/benefit-activity-dashboard/saga-actions";
import { BenefitActivityDashboardPartner } from "../../../../../../redux/slices/partner-program/benefit-activity-dashboard/Types";
import IntegrationColumnsDropdown from "../integration-status/IntegrationColumnsDropdown";
import ViewCommentsModal from "../../manage-partner/partner-program/benefits-log/ViewCommentsModal";
import useBenefitMediaPreview from "../../manage-partner/partner-program/benefits-log/useBenefitMediaPreview";
import { BenefitLogHistoryRow } from "../../manage-partner/partner-program/benefits-log/mockData";
import {
  mapActivityLogToHistoryRow,
  statusClassName,
} from "../../manage-partner/partner-program/benefits-log/benefitsLogUtils";
import BenefitsLogFilterModal from "../../manage-partner/partner-program/benefits-log/BenefitsLogFilterModal";
import useBenefitLogFilterOptions from "../../manage-partner/partner-program/benefits-log/useBenefitLogFilterOptions";
import {
  BENEFIT_ACTIVITY_COLUMNS,
  BENEFIT_ACTIVITY_TILE_STATUS,
  BenefitActivityFilterState,
  DEFAULT_VISIBLE_BENEFIT_ACTIVITY_COLUMNS,
  EMPTY_BENEFIT_ACTIVITY_FILTER,
  hasActiveBenefitActivityFilters,
} from "./benefitActivityLogColumns";
import { KPI_TILE_COLORS } from "../kpiTileColors";
import "../../manage-partner/partner-program/benefits-log/BenefitsLog.scss";
import "./BenefitActivityLogTab.scss";

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

const MAX_VISIBLE_BENEFITS = 5;

type Props = {
  onOpenPartner?: (partnerId: number) => void;
  canOpenPartner?: boolean;
  /** Opens the partner's Benefits Log tab when a partner has more than MAX_VISIBLE_BENEFITS. */
  onViewAllBenefits?: (partnerId: number) => void;
  canViewAllBenefits?: boolean;
};

const BenefitActivityLogTab: React.FC<Props> = ({
  onOpenPartner,
  canOpenPartner = false,
  onViewAllBenefits,
  canViewAllBenefits = false,
}) => {
  const dispatch = useAppDispatch();
  const { summary, partners, pagination, status } = useAppSelector(
    (state) => state.benefit_activity_dashboard
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [appliedSearch, setAppliedSearch] = React.useState("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [filter, setFilter] = React.useState<BenefitActivityFilterState>(
    EMPTY_BENEFIT_ACTIVITY_FILTER
  );
  const [filterModalOpen, setFilterModalOpen] = React.useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = React.useState<string[]>(
    DEFAULT_VISIBLE_BENEFIT_ACTIVITY_COLUMNS
  );
  const [expandedPartnerIds, setExpandedPartnerIds] = React.useState<Set<number>>(
    () => new Set()
  );
  const [viewModalRow, setViewModalRow] = React.useState<BenefitLogHistoryRow | null>(
    null
  );
  const [toasts, setToasts] = React.useState<any[]>([]);

  const [partnerSortOrder, setPartnerSortOrder] = React.useState<"asc" | "desc" | null>(null);

  const showErrorToast = React.useCallback((message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant: NotificationVariants.Error,
        icon: <Iconography icon="ban" size={IconographySizes.Medium} />,
      },
    ]);
  }, []);

  const handleOpenFile = useBenefitMediaPreview(showErrorToast);

  const filterOptions = useBenefitLogFilterOptions(
    { type: "dashboard" },
    true,
    filterModalOpen
  );

  const toLabelMap = (options: { label: string; value: string }[]) =>
    new Map(options.map((option) => [option.value, option.label]));
  const partnerNameById = React.useMemo(
    () => toLabelMap(filterOptions.partners),
    [filterOptions.partners]
  );
  const benefitNameById = React.useMemo(
    () => toLabelMap(filterOptions.benefits),
    [filterOptions.benefits]
  );
  const hidOwnerNameById = React.useMemo(
    () => toLabelMap(filterOptions.hidOwners),
    [filterOptions.hidOwners]
  );
  const partnerOwnerNameById = React.useMemo(
    () => toLabelMap(filterOptions.partnerOwners),
    [filterOptions.partnerOwners]
  );

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setAppliedSearch(searchTerm.trim());
      setCurrentPageNumber(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const partnerIdsParam = filter.partner.join(",");
  const benefitIdsParam = filter.benefitsName.join(",");
  const statusesParam = filter.status.join(",");
  const hidUserIdsParam = filter.hidOwner.join(",");
  const partnerUserIdsParam = filter.partnerOwner.join(",");
  const [deliveredFrom = "", deliveredTo = ""] = filter.deliveredDateRange;
  const [plannedFrom = "", plannedTo = ""] = filter.plannedDateRange;

  React.useEffect(() => {
    dispatch(
      getBenefitActivityDashboardSagaAction({
        has_benefits: true,
        partner_ids: partnerIdsParam || undefined,
        benefit_ids: benefitIdsParam || undefined,
        statuses: statusesParam || undefined,
        hid_user_ids: hidUserIdsParam || undefined,
        partner_user_ids: partnerUserIdsParam || undefined,
        delivered_date_from: deliveredFrom || undefined,
        delivered_date_to: deliveredTo || undefined,
        planned_date_from: plannedFrom || undefined,
        planned_date_to: plannedTo || undefined,
        search: appliedSearch || undefined,
        page: currentPageNumber,
        limit: pageSize,
      })
    );
    setExpandedPartnerIds(new Set());
  }, [
    dispatch,
    partnerIdsParam,
    benefitIdsParam,
    statusesParam,
    hidUserIdsParam,
    partnerUserIdsParam,
    deliveredFrom,
    deliveredTo,
    plannedFrom,
    plannedTo,
    appliedSearch,
    currentPageNumber,
    pageSize,
  ]);

const displayedPartners = React.useMemo(() => {
  const mappedPartners = partners.map((partner) => ({
    partner,
    rows: partner.logs.map(mapActivityLogToHistoryRow),
  }));

  if (!partnerSortOrder) {
    return mappedPartners;
  }

  return [...mappedPartners].sort((a, b) => {
    const partnerA = a.partner.partnerName?.toLowerCase() || "";
    const partnerB = b.partner.partnerName?.toLowerCase() || "";

    return partnerSortOrder === "asc"
      ? partnerA.localeCompare(partnerB)
      : partnerB.localeCompare(partnerA);
  });
}, [partners, partnerSortOrder]);

  const tiles = [
    { id: "total", label: "Total Benefits", value: summary?.total ?? 0, color: KPI_TILE_COLORS.blue },
    { id: "delivered", label: "Delivered", value: summary?.delivered ?? 0, color: KPI_TILE_COLORS.green },
    { id: "inProgress", label: "In Progress", value: summary?.inProgress ?? 0, color: KPI_TILE_COLORS.orange },
    { id: "overdue", label: "Overdue", value: summary?.overdue ?? 0, color: KPI_TILE_COLORS.red },
  ];

  const activeTileId = React.useMemo(() => {
    if (filter.status.length === 0) return "total";
    if (filter.status.length !== 1) return null;
    const match = Object.entries(BENEFIT_ACTIVITY_TILE_STATUS).find(
      ([, value]) => value === filter.status[0]
    );
    return match?.[0] ?? null;
  }, [filter.status]);

  const handleTileClick = (tileId: string) => {
    const statusValue = BENEFIT_ACTIVITY_TILE_STATUS[tileId];
    const isSelected =
      statusValue !== null &&
      filter.status.length === 1 &&
      filter.status[0] === statusValue;
    setFilter((prev) => ({
      ...prev,
      status: statusValue === null || isSelected ? [] : [statusValue],
    }));
    setCurrentPageNumber(1);
  };

  const handleApplyFilter = (nextFilter: BenefitActivityFilterState) => {
    setFilter(nextFilter);
    setCurrentPageNumber(1);
  };

  const removeFilterValue = (field: keyof BenefitActivityFilterState, value: string) => {
    setFilter((prev) => ({
      ...prev,
      [field]: prev[field].filter((item) => item !== value),
    }));
    setCurrentPageNumber(1);
  };

  const clearFilterField = (field: keyof BenefitActivityFilterState) => {
    setFilter((prev) => ({ ...prev, [field]: [] }));
    setCurrentPageNumber(1);
  };

  const formatTagDate = (value: string) => {
    const [year, month, day] = value.split("-");
    return year && month && day ? `${day}/${month}/${year}` : value;
  };

  const formatRangeTag = ([from = "", to = ""]: string[]) => {
    if (from && to) return `${formatTagDate(from)} - ${formatTagDate(to)}`;
    if (from) return `From ${formatTagDate(from)}`;
    return `Until ${formatTagDate(to)}`;
  };

  const handleSearch = (_event: unknown, value: string) => {
    setSearchTerm(value);
    setAppliedSearch(value.trim());
    setCurrentPageNumber(1);
  };

  const handlePageChange = (clickedPageNumber: number | null) => {
    if (clickedPageNumber != null) setCurrentPageNumber(clickedPageNumber);
  };

  const onPageSizesClick = (_event: unknown, value: string) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  const togglePartnerExpanded = (partnerId: number) => {
    setExpandedPartnerIds((prev) => {
      const next = new Set(prev);
      if (next.has(partnerId)) next.delete(partnerId);
      else next.add(partnerId);
      return next;
    });
  };

  const hasActiveFilters = hasActiveBenefitActivityFilters(filter);
  const visibleColumns = BENEFIT_ACTIVITY_COLUMNS.filter((column) =>
    visibleColumnKeys.includes(column.key)
  );

  if (status.loading && !summary) {
    return (
      <div className="benefit-activity-log benefit-activity-log--loading">
        <Loader variant={LoaderSizes.large} />
      </div>
    );
  }

  const handlePartnerSort = () => {
  setPartnerSortOrder((prev) => {
    if (prev === null) return "asc";
    if (prev === "asc") return "desc";
    return null;
  });
};


  return (
    <div className="benefit-activity-log">
      <Notification toasts={toasts} setToasts={setToasts} />
      <div className="partner-summary-cards">
        {tiles.map((tile) => (
          <button
            type="button"
            className={`partner-summary-card${
              activeTileId === tile.id ? " partner-summary-card--selected" : ""
            }`}
            key={tile.id}
            onClick={() => handleTileClick(tile.id)}
            aria-pressed={activeTileId === tile.id}
          >
            <span className="partner-summary-card__label">{tile.label}</span>
            <span className="partner-summary-card__footer">
              <span className="partner-summary-card__value">{tile.value}</span>
              <span
                className="partner-summary-card__dot"
                style={{ backgroundColor: tile.color }}
                aria-hidden
              />
            </span>
          </button>
        ))}
      </div>

      <div className="partner-toolbar">
        <div className="search-input-filter-wrapper">
          <div className="left">
            <SearchInput
              theme="dark"
              id="searchBenefitActivityPartner"
              name="searchBenefitActivityPartner"
              placeholder="Search by partner, comments or status..."
              isDisplayClearIcon={true}
              value={searchTerm}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(event.currentTarget.value)
              }
              onSearch={handleSearch}
            />
          </div>
          <div className="center">
            <button
              type="button"
              className={`toolbar-link filter-link${
                hasActiveFilters ? " toolbar-link--active" : ""
              }`}
              onClick={() => setFilterModalOpen(true)}
            >
              <Iconography icon="filter" size={IconographySizes.Small} />
              <span>Filter</span>
            </button>
            <IntegrationColumnsDropdown
              availableColumns={BENEFIT_ACTIVITY_COLUMNS}
              visibleColumnKeys={visibleColumnKeys}
              onVisibleColumnKeysChange={setVisibleColumnKeys}
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filter-tag-wrapper">
            {filter.partner.map((value) => (
              <Tag
                key={`partner-${value}`}
                value={`Partner: ${partnerNameById.get(value) ?? value}`}
                appearance={TagAppearance.Blue}
                isCloseIcon={true}
                onCloseClick={() => removeFilterValue("partner", value)}
              />
            ))}
            {filter.benefitsName.map((value) => (
              <Tag
                key={`benefit-${value}`}
                value={`Benefit: ${benefitNameById.get(value) ?? value}`}
                appearance={TagAppearance.Blue}
                isCloseIcon={true}
                onCloseClick={() => removeFilterValue("benefitsName", value)}
              />
            ))}
            {filter.status.map((value) => (
              <Tag
                key={`status-${value}`}
                value={`Status: ${value}`}
                appearance={TagAppearance.Blue}
                isCloseIcon={true}
                onCloseClick={() => removeFilterValue("status", value)}
              />
            ))}
            {filter.hidOwner.map((value) => (
              <Tag
                key={`hid-owner-${value}`}
                value={`HID Owner: ${hidOwnerNameById.get(value) ?? value}`}
                appearance={TagAppearance.Blue}
                isCloseIcon={true}
                onCloseClick={() => removeFilterValue("hidOwner", value)}
              />
            ))}
            {filter.partnerOwner.map((value) => (
              <Tag
                key={`partner-owner-${value}`}
                value={`Partner Owner: ${partnerOwnerNameById.get(value) ?? value}`}
                appearance={TagAppearance.Blue}
                isCloseIcon={true}
                onCloseClick={() => removeFilterValue("partnerOwner", value)}
              />
            ))}
            {filter.deliveredDateRange.length > 0 && (
              <Tag
                value={`Delivered: ${formatRangeTag(filter.deliveredDateRange)}`}
                appearance={TagAppearance.Blue}
                isCloseIcon={true}
                onCloseClick={() => clearFilterField("deliveredDateRange")}
              />
            )}
            {filter.plannedDateRange.length > 0 && (
              <Tag
                value={`Planned: ${formatRangeTag(filter.plannedDateRange)}`}
                appearance={TagAppearance.Blue}
                isCloseIcon={true}
                onCloseClick={() => clearFilterField("plannedDateRange")}
              />
            )}
          </div>
        )}
      </div>

      <div className="table-card benefit-activity-table-card">
        {status.error && (
          <div className="no-data benefit-activity-log__error">{status.error}</div>
        )}
        {status.loading ? (
          <div className="benefit-activity-log__table-loader">
            <Loader variant={LoaderSizes.medium} />
          </div>
        ) : (
          <>
            <div className="benefit-activity-table">
              <div className="benefit-activity-table__header">
                <div className="benefit-activity-table__header-partner"
                  onClick={handlePartnerSort}
                  style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                >
                  Partner Name

                  <Iconography
                    icon={
                      partnerSortOrder === "asc"
                        ? "angleUp"
                        : partnerSortOrder === "desc"
                        ? "angleDown"
                        : "sort"
                    }
                    size={IconographySizes.Small}
                  />
                </div>
                <div className="benefit-activity-table__header-status">
                  Delivery Status
                </div>
                <div aria-hidden />
              </div>

              {displayedPartners.length === 0 ? (
                <div className="no-data">
                  {appliedSearch || hasActiveFilters
                    ? "No partners match your search or filters."
                    : "No data available for display."}
                </div>
              ) : (
                displayedPartners.map(({ partner, rows }) => (
                  <BenefitActivityPartnerBlock
                    key={partner.partnerId}
                    partner={partner}
                    rows={rows}
                    visibleColumns={visibleColumns}
                    isExpanded={expandedPartnerIds.has(partner.partnerId)}
                    onToggleExpand={() => togglePartnerExpanded(partner.partnerId)}
                    onOpenPartner={onOpenPartner}
                    canOpenPartner={canOpenPartner}
                    onViewAllBenefits={onViewAllBenefits}
                    canViewAllBenefits={canViewAllBenefits}
                    onViewComments={setViewModalRow}
                  />
                ))
              )}
            </div>
            {pagination && pagination.totalRecords > 0 && (
              <Pagination
                currentPageNumber={pagination.currentPage}
                totalRecords={pagination.totalRecords}
                pageSize={pagination.perPage}
                onPageNumberClick={handlePageChange}
                onPageSizesClick={onPageSizesClick}
                pageSizes={[
                  { label: "20", value: "20" },
                  { label: "50", value: "50" },
                  { label: "100", value: "100" },
                ]}
              />
            )}
          </>
        )}
      </div>

      {filterModalOpen && (
        <BenefitsLogFilterModal
          title="Filter Benefit Activity Log"
          filterProp={filter}
          setFilterProp={handleApplyFilter}
          setModalOpen={setFilterModalOpen}
          partnerOptions={filterOptions.partners}
          benefitNameOptions={filterOptions.benefits}
          statusOptions={filterOptions.statuses}
          hidOwnerOptions={filterOptions.hidOwners}
          partnerOwnerOptions={filterOptions.partnerOwners}
          keepOpenEndedDateRanges
        />
      )}

      <ViewCommentsModal
        open={Boolean(viewModalRow)}
        benefitName={viewModalRow?.benefit || ""}
        comment={viewModalRow?.comment || ""}
        files={viewModalRow?.files || []}
        onFileClick={handleOpenFile}
        onClose={() => setViewModalRow(null)}
      />
    </div>
  );
};

type BenefitActivityPartnerBlockProps = {
  partner: BenefitActivityDashboardPartner;
  rows: BenefitLogHistoryRow[];
  visibleColumns: typeof BENEFIT_ACTIVITY_COLUMNS;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOpenPartner?: (partnerId: number) => void;
  canOpenPartner: boolean;
  onViewAllBenefits?: (partnerId: number) => void;
  canViewAllBenefits: boolean;
  onViewComments: (row: BenefitLogHistoryRow) => void;
};

const BenefitActivityPartnerBlock: React.FC<BenefitActivityPartnerBlockProps> = ({
  partner,
  rows,
  visibleColumns,
  isExpanded,
  onToggleExpand,
  onOpenPartner,
  canOpenPartner,
  onViewAllBenefits,
  canViewAllBenefits,
  onViewComments,
}) => {
  const hasMoreBenefits = rows.length > MAX_VISIBLE_BENEFITS;
  const visibleRows = hasMoreBenefits ? rows.slice(0, MAX_VISIBLE_BENEFITS) : rows;

  const renderCell = (row: BenefitLogHistoryRow, columnKey: string) => {
    switch (columnKey) {
      case "plannedDate":
        return row.plannedDate;
      case "deliveredDate":
        return row.deliveredDate;
      case "status":
        return (
          <span className={`bl-status-badge ${statusClassName(row.status)}`}>
            {row.status}
          </span>
        );
      case "hidOwner":
        return row.hidOwner;
      case "partnerOwner":
        return row.partnerOwner;
      case "comments":
        return row.hasCommentsOrFiles ? (
          <button
            type="button"
            className="benefit-activity-details__view-link"
            onClick={() => onViewComments(row)}
          >
            View
          </button>
        ) : (
          "-"
        );
      default:
        return null;
    }
  };

  return (
    <div className="benefit-activity-table__partner-group">
      <div
        className={`benefit-activity-table__partner-summary${
          isExpanded ? " is-expanded" : ""
        }`}
      >
        <div className="benefit-activity-table__partner-cell">
          <button
            type="button"
            className="benefit-activity-table__expand-btn"
            onClick={onToggleExpand}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${partner.partnerName}`}
          >
            <Iconography
              icon={isExpanded ? "angleUp" : "angleDown"}
              size={IconographySizes.Small}
            />
          </button>
          <button
            type="button"
            className="benefit-activity-table__partner-name"
            onClick={() => onOpenPartner?.(partner.partnerId)}
            disabled={!canOpenPartner}
          >
            {partner.partnerName}
          </button>
        </div>
        <div
          className={`benefit-activity-table__delivery-status benefit-activity-table__delivery-status--${
            partner.isDelivered ? "delivered" : "not-delivered"
          }`}
        >
          {partner.deliveryStatusLabel}
        </div>
        {hasMoreBenefits ? (
          <button
            type="button"
            className="benefit-activity-table__view-all"
            onClick={() => onViewAllBenefits?.(partner.partnerId)}
            disabled={!canViewAllBenefits || !onViewAllBenefits}
          >
            View All
          </button>
        ) : (
          <div aria-hidden />
        )}
      </div>

      {isExpanded && (
        <div className="benefit-activity-table__expanded">
          {rows.length === 0 ? (
            <div className="benefit-activity-details__empty">
              No benefits logged for this partner.
            </div>
          ) : (
            <div className="benefit-activity-details">
              <Table size="small" className="benefit-activity-details__table">
                <TableHead>
                  <TableRow>
                    <TableCell className="benefit-activity-details__col-benefit">
                      Benefits
                    </TableCell>
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={`benefit-activity-details__col-${column.key}`}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="benefit-activity-details__col-benefit">
                        {row.benefit}
                      </TableCell>
                      {visibleColumns.map((column) => (
                        <TableCell
                          key={column.key}
                          className={`benefit-activity-details__col-${column.key}`}
                        >
                          {renderCell(row, column.key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BenefitActivityLogTab;
